import { useEffect, useRef } from "react";

/**
 * useLightbox - React hook for lightbox/modal open/close, scroll lock, and keyboard/escape handling
 * @param isOpen - Whether the lightbox/modal is open
 * @param onClose - Function to call when the lightbox should close (Escape, popstate)
 */
export function useLightbox(isOpen: boolean, onClose: () => void) {
  const historyPushedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    // Lock scroll
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";

    // Escape key handler
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    // Popstate handler (browser back)
    const handlePopState = () => onClose();
    window.addEventListener("popstate", handlePopState);
    
    // Only push state if we haven't already for this lightbox session
    if (!historyPushedRef.current) {
      window.history.pushState({ lightbox: true }, "");
      historyPushedRef.current = true;
    }

    return () => {
      document.body.style.overflow = originalStyle;
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isOpen, onClose]);

  // Separate effect to handle history cleanup when lightbox closes
  useEffect(() => {
    if (!isOpen && historyPushedRef.current) {
      // Only go back if we pushed a state and lightbox is now closed
      if (window.history.state && window.history.state.lightbox) {
        window.history.back();
      }
      historyPushedRef.current = false;
    }
  }, [isOpen]);
}
